'use client';

import React, { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Platform = 'ios' | 'android-chrome' | 'edge' | 'other' | null;

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent;
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isEdge = /edg\//i.test(ua);
  const isChrome = /chrome/i.test(ua) && !/edg\//i.test(ua);
  const isAndroid = /android/i.test(ua);
  if (isIOS) return 'ios';
  if (isEdge) return 'edge';
  if (isChrome && isAndroid) return 'android-chrome';
  return 'other';
}

function isInStandaloneMode(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true)
  );
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [platform, setPlatform] = useState<Platform>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isInStandaloneMode()) {
      setInstalled(true);
      return;
    }

    const p = detectPlatform();
    setPlatform(p);

    const dismissed = sessionStorage.getItem('pwa-install-dismissed');
    if (dismissed) return;

    // Listen for the browser's native install prompt (Chrome Android, Edge)
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // For iOS: show the manual guide after a short delay
    if (p === 'ios') {
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
        clearTimeout(timer);
      };
    }

    // For other browsers without native prompt, show after delay
    if (p === 'other') {
      const timer = setTimeout(() => setShowBanner(true), 5000);
      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
        clearTimeout(timer);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (platform === 'ios') {
      setShowIOSGuide(true);
      return;
    }
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstalled(true);
      }
      setDeferredPrompt(null);
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem('pwa-install-dismissed', '1');
    setShowBanner(false);
    setShowIOSGuide(false);
  };

  if (installed || !showBanner) return null;

  return (
    <>
      {/* Install Banner */}
      <div
        className="fixed bottom-20 left-4 right-4 z-50 rounded-2xl shadow-lg border border-[#6B8F71]/20 bg-white/95 backdrop-blur-sm p-4 flex items-center gap-3"
        role="dialog"
        aria-label="Install app prompt"
      >
        {/* App icon */}
        <div className="w-12 h-12 rounded-xl bg-[#6B8F71]/10 flex items-center justify-center flex-shrink-0">
          <img
            src="/assets/images/clingy-favicon.png"
            alt="Clingy app icon"
            className="w-8 h-8 rounded-lg"
          />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 leading-tight">Install My Little Clingy</p>
          <p className="text-xs text-gray-500 mt-0.5 leading-snug">
            {platform === 'ios' ?'Add to your Home Screen for the best experience' :'Add to your home screen for quick access'}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1"
            aria-label="Dismiss install prompt"
          >
            Not now
          </button>
          <button
            onClick={handleInstall}
            className="text-xs font-semibold text-white bg-[#6B8F71] hover:bg-[#5a7a60] px-3 py-1.5 rounded-lg transition-colors"
          >
            {platform === 'ios' ? 'How to' : 'Install'}
          </button>
        </div>
      </div>

      {/* iOS Step-by-step guide modal */}
      {showIOSGuide && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm"
          onClick={handleDismiss}
        >
          <div
            className="w-full max-w-sm bg-white rounded-t-3xl p-6 pb-10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <h2 className="text-base font-bold text-gray-900 mb-1">Add to Home Screen</h2>
            <p className="text-sm text-gray-500 mb-5">Follow these steps in Safari:</p>

            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#6B8F71]/15 text-[#6B8F71] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                <div>
                  <p className="text-sm font-medium text-gray-800">Tap the Share button</p>
                  <p className="text-xs text-gray-500 mt-0.5">The <span className="font-semibold">⬆</span> icon at the bottom of Safari</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#6B8F71]/15 text-[#6B8F71] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                <div>
                  <p className="text-sm font-medium text-gray-800">Scroll down and tap</p>
                  <p className="text-xs text-gray-500 mt-0.5"><span className="font-semibold">"Add to Home Screen"</span> in the share sheet</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#6B8F71]/15 text-[#6B8F71] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                <div>
                  <p className="text-sm font-medium text-gray-800">Tap "Add"</p>
                  <p className="text-xs text-gray-500 mt-0.5">Clingy will appear on your Home Screen</p>
                </div>
              </li>
            </ol>

            <button
              onClick={handleDismiss}
              className="mt-6 w-full py-3 rounded-2xl bg-[#6B8F71] text-white text-sm font-semibold hover:bg-[#5a7a60] transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
